import {Injectable} from '@angular/core';
import {CapacitorSQLite, SQLiteConnection, SQLiteDBConnection} from '@capacitor-community/sqlite';
import {environment} from "src/app/environments/environment";
import {Asset, Persistable, SynchronizationStatus} from "src/app/infrastructure/moovement-api/models/asset";
import {AssetMigrationStatements} from "src/app/infrastructure/storage/migrations/asset.migration.statements";
import {
  KeyValuePairsMigrationStatements
} from "src/app/infrastructure/storage/migrations/key-value-pair.migrations.statements";
import {WebStorageService} from "src/app/infrastructure/web-storage.service";
import {LocationAgeFilterPeriod} from 'src/app/domain/map/map.store';
import {
  ScheduledRequestMigrationStatements
} from 'src/app/infrastructure/storage/migrations/scheduled-request.migration.statements';
import {ScheduledRequest} from 'src/app/infrastructure/scheduled-requests/scheduled-requests';
import {DataType} from 'src/app/domain/domain-models';
import {
  ScheduledUpdatesMigrationStatements
} from 'src/app/infrastructure/storage/migrations/scheduled-updates.migration.statements';
import {ScheduledUpdates} from 'src/app/infrastructure/scheduled-updates/scheduled-updates';
import {
  DeadLetterMigrationStatements
} from 'src/app/infrastructure/storage/migrations/dead-letter.migration.statements';
import {DeadLetterEntry} from "src/app/infrastructure/moovement-api/models/dead-letter";

const connectionName = 'moovement';
const version = 0;

const sqlite = new SQLiteConnection(CapacitorSQLite);

const keysToPreserve = ['authToken', 'user', 'userAgent'];

async function getOrCreateConnection(): Promise<SQLiteDBConnection> {

  await sqlite.closeConnection(connectionName, false).catch(() => {
    // Ignore if already closed
  });

  const db = await sqlite.createConnection(connectionName, false, 'no-encryption', version, false);

  await db.open();
  return db;
}

//This service only works in native environments, not in a browser.
// In a browser all methods will return default/empty values and storage will be delegated to the webStorageService.
@Injectable({
  providedIn: 'root',
})
export class DataStorageService {
  private dbConnection: SQLiteDBConnection | undefined;
  private assetMigrationsStatements: AssetMigrationStatements = new AssetMigrationStatements();
  private keyValuePairMigrationStatements: KeyValuePairsMigrationStatements = new KeyValuePairsMigrationStatements();
  private scheduledRequestMigrationStatements: ScheduledRequestMigrationStatements = new ScheduledRequestMigrationStatements();

  private scheduledUpdatessMigrationStatements: ScheduledUpdatesMigrationStatements = new ScheduledUpdatesMigrationStatements();
  private deadLetterMigrationStatements: DeadLetterMigrationStatements = new DeadLetterMigrationStatements();

  constructor(
    private webStorageService: WebStorageService
  ) {}

  async openDatabaseConnection() {
    if (['web', ''].includes(environment.platform)) return;

    try {
      const isConnected = await this.isConnected();
      if (isConnected) return;

      this.dbConnection = await getOrCreateConnection();

      await this.dbConnection.execute(this.assetMigrationsStatements.migrations[version]!);
      await this.dbConnection.execute(this.keyValuePairMigrationStatements.migrations[version]!);
      await this.dbConnection.execute(this.scheduledRequestMigrationStatements.migrations[version]!);
      await this.dbConnection.execute(this.scheduledUpdatessMigrationStatements.migrations[version]!);
      await this.dbConnection.execute(this.deadLetterMigrationStatements.migrations[version]!);
    } catch (error) {
      console.error('Error initializing database', error);
    }
  }

  async isConnected() {
    const dbConnectionExists = this.dbConnection != undefined
    const dbConnectionIsOpen = (await this.dbConnection?.isDBOpen())?.result || false
    return dbConnectionExists && dbConnectionIsOpen
  }

  async saveAsset<P extends Persistable>(dataType: DataType, farmId: string, asset: P, synchronizationStatus: SynchronizationStatus, id: string = asset.id) {
    if (environment.platform == 'web') return;

    asset.synchronizationStatus = synchronizationStatus;
    const bodyString = JSON.stringify(asset)
    const result = await this.dbConnection?.run(
      `INSERT OR
       REPLACE
       INTO asset (id, dataType, assetState, json, farmId)
       VALUES (?, ?, ?, ?, ?)`,
      [id, dataType, synchronizationStatus, bodyString, farmId]
    );
    if (!result || !result?.changes?.changes || result!.changes!.changes < 1) {
      return Promise.reject(`Failed to merge ${dataType} for ${farmId}.`);
    }
  }

  async persistScheduledRequest<D>(request: ScheduledRequest<D>) {
    if (environment.platform == 'web') return;

    const bodyString = JSON.stringify(request);
    const result = await this.dbConnection?.run(
      `INSERT OR REPLACE
       INTO scheduled_request (id, json)
       VALUES (?, ?)`,
      [request.id, bodyString]
    );
    if (!result || !result?.changes?.changes || result!.changes!.changes < 1) {
      return Promise.reject(`Failed to persist scheduled request ${request}`);
    }
  }

  async getScheduledRequests<D>() {
    if (environment.platform == 'web') return [];

    try {
      const result = await this.dbConnection?.query('SELECT json FROM scheduled_request');
      return result?.values?.map((entity: { json: string }) => JSON.parse(entity.json) as ScheduledRequest<D>) || [];
    } catch (error) {
      throw error;
    }
  }

  async deleteScheduledRequest(id: string) {
    if (environment.platform == 'web') return;

    await this.dbConnection?.run(
      `DELETE
        FROM scheduled_request
        WHERE id = ?`, [id],
    )
  }


  async persistScheduledUpdates(updates: ScheduledUpdates) {
    if (environment.platform == 'web') return;

    const bodyString = JSON.stringify(updates);
    const result = await this.dbConnection?.run(
      `INSERT OR REPLACE
       INTO scheduled_updates (id, json)
       VALUES (?, ?)`,
      [updates.id, bodyString]
    );
    if (!result || !result?.changes?.changes || result!.changes!.changes < 1) {
      return Promise.reject(`Failed to persist scheduled updates ${updates}`);
    }
  }

  async getScheduledUpdates() {
    if (environment.platform == 'web') return [];

    try {
      const result = await this.dbConnection?.query('SELECT json FROM scheduled_updates');
      return result?.values?.map((entity: { json: string }) => JSON.parse(entity.json) as ScheduledUpdates) || [];
    } catch (error) {
      throw error;
    }
  }

  async deleteScheduledUpdates(id: string) {
    if (environment.platform == 'web') return;

    await this.dbConnection?.run(
      `DELETE
        FROM scheduled_updates
        WHERE id = ?`, [id],
    )
  }

  async getAllAssetsMatching<P extends Persistable>(dataType: DataType, farmId: string, synchronizationStatus: SynchronizationStatus): Promise<P[]> {
    if (environment.platform == 'web') return [];

    const result = await this.dbConnection?.query(
      'SELECT * FROM asset WHERE dataType = ? AND farmId = ? AND assetState = ?',
      [dataType, farmId, synchronizationStatus]
    );
    return result?.values?.map((entity: AssetEntity) => JSON.parse(entity.json) as P) || [];
  }

  async getAllActiveAssetsMatching<P extends Persistable>(dataType: DataType, farmId: string): Promise<P[]> {
    if (environment.platform == 'web') return [];

    const result = await this.dbConnection?.query(
      'SELECT * FROM asset WHERE dataType = ? AND farmId = ? AND assetState != ?',
      [dataType, farmId, SynchronizationStatus.deleted]
    );
    return result?.values?.map((entity: AssetEntity) => {
      const obj = JSON.parse(entity.json) as P
      obj.synchronizationStatus = entity.synchronizationStatus
      return obj
    }) || [];
  }

  async deleteAsset(id: string): Promise<void> {
    if (environment.platform == 'web') return;

    await this.dbConnection?.run(
      `DELETE
        FROM asset
        WHERE id = ?`, [id],
    )
  }

  async addKeyValuePair<T>(key: string, value: T) {
    if (value === undefined || value === null) {
      console.log("Empty value provided to database")
      return;
    }
    if (environment.platform == 'web' && this.webStorageService.shouldStore(key)) {
      await this.webStorageService.store(key, value)
      return;
    }

    if (environment.platform == 'web') return;

    const stringifiedValue = JSON.stringify(value)
    const result = await this.dbConnection?.run(
      `INSERT OR
       REPLACE
       INTO key_value_pair (id, json)
       VALUES (?, ?)`, [key, stringifiedValue],
    )
    if (!result || !result?.changes?.changes || result!.changes!.changes < 1) {
      console.error(`Failed to store ${key} in database, connection might be closed`);
    }
  }

  async setLocationAgeFilterPeriod(period: LocationAgeFilterPeriod) {
    await this.addKeyValuePair('locationAgeFilterPeriod', period);
  }

  async getLocationAgeFilterPeriod(): Promise<LocationAgeFilterPeriod> {
    const period = await this.getValueByKey<LocationAgeFilterPeriod>('locationAgeFilterPeriod');
    return period || LocationAgeFilterPeriod.LAST_60_DAYS;
  }

  async removeKeyValue(key: string): Promise<void> {
    if (await this.webStorageService.hasStored(key)) {
      await this.webStorageService.removeStored(key)
    }
    if (environment.platform == 'web') {
      return
    }

    await this.dbConnection?.run(
      `DELETE
       FROM key_value_pair
       WHERE id = ?`, [key])
  }

  async clearAllAssets(): Promise<void> {
    if (environment.platform == 'web') return;

    try {
      await this.dbConnection?.run(
        `DELETE
         FROM asset`
      )
    } catch (error: any) {
      console.error(`Error removing assets ${error || 'no-message'}`);
      return Promise.reject(error);
    }
  }

  async getValueByKey<T>(key: string): Promise<T | undefined> {
    if (environment.platform == 'web' && this.webStorageService.shouldStore(key)) {
        return await this.webStorageService.getStored<T | undefined>(key)
    }

    const valueResult = await this.dbConnection?.query(
      'SELECT * FROM key_value_pair WHERE id = ?', [key])
    const result = valueResult?.values as KeyValuePairEntity[]

    if (result && result.length > 0) {
      return JSON.parse(result[0].json) as T | undefined
    }
    return undefined
  }

  async saveDeadLetterEntry(entry: DeadLetterEntry): Promise<void> {
    if (environment.platform == 'web') return;

    const result = await this.dbConnection?.run(
      `INSERT OR REPLACE INTO dead_letter (id, source, json, failedAt, reason) VALUES (?, ?, ?, ?, ?)`,
      [entry.id, entry.source, entry.json, entry.failedAt, entry.reason]
    );
    if (!result || !result?.changes?.changes || result!.changes!.changes < 1) {
      return Promise.reject(`Failed to save dead letter entry ${entry.id}`);
    }
  }

  async getDeadLetterEntries(): Promise<DeadLetterEntry[]> {
    if (environment.platform == 'web') return [];

    const result = await this.dbConnection?.query('SELECT * FROM dead_letter');
    return result?.values?.map((row: DeadLetterEntry) => row) || [];
  }

  async deleteDeadLetterEntry(id: string): Promise<void> {
    if (environment.platform == 'web') return;

    await this.dbConnection?.run(`DELETE FROM dead_letter WHERE id = ?`, [id]);
  }

  async getAssetsDump<A extends Asset>(dataType: string, farmId: string): Promise<A[]> {
    if (environment.platform == 'web') return [];

    const result = await this.dbConnection?.query(
      'SELECT * FROM asset WHERE farmId = ? AND dataType = ?',
      [farmId, dataType]
    );
    return result?.values?.map((entity: AssetEntity) => {
      const obj = JSON.parse(entity.json) as A
      obj.synchronizationStatus = entity.synchronizationStatus
      return obj
    }) || [];
  }

  async getKeyValuePairsDump(): Promise<(KeyValuePairEntity)[]> {
    if (environment.platform == 'web') return [];

    const valueResult = await this.dbConnection?.query(
      'SELECT * FROM key_value_pair'
    );
    return valueResult?.values as KeyValuePairEntity[] || []
  }

  async clearKeyValuePairs() {
    if (environment.platform == 'web') return;

    try {
      const placeholders = keysToPreserve.map(_ => '?').join(',');
      await this.dbConnection?.run(
        `DELETE
         FROM key_value_pair
         WHERE id NOT IN (${placeholders})`,
         keysToPreserve
      );
    } catch (error: any) {
      console.error(`DB: Error removing key/value pairs ${error || 'no-message'}`);
      return Promise.reject(error);
    }
  }
}

export interface KeyValuePairEntity {
  id: string;
  json: string;
}

export interface AssetEntity {
  id: string;
  dataType: string;
  farmId: string;
  json: string;
  synchronizationStatus: SynchronizationStatus;
}
